import utils from './utils';

/**
 * Cleans the transaction row by removing and renaming based on the columnMap
 * @param row {Object} The row for a transaction
 * @returns {Object} The new row
 */
function cleanTransactionRow(row) {
  const columnMap = {
    transactionId: 'Id',
    createdAt: 'Created',
    type: 'Type',
    source: 'Source',
    amount: 'Amount',
    taxAmount: 'Tax',
    unitQuantity: 'Unit Qty',
    planId: 'Plan Id',
    productsText: 'Products',
    stripe: 'Stripe',
    description: 'Description',
  };
  return Object.entries(row)
    .reduce((accumulator, [key, value]) => {
      const newRow = accumulator;
      if (Object.keys(columnMap).includes(key)) {
        newRow[columnMap[key]] = value;
      }
      return newRow;
    }, {});
}

/**
 * Returns the html display for the AG Grid cell for manual transaction. For
 * non-manual transactions, it will just return the source.
 * @param source {string} The transaction source. E.g.: manual or monolith.
 */
function getManualSourceHtml(source) {
  const style = (source === 'manual')
    ? 'background-color: #ffffcc; color: #000000; padding-right: 7px; padding-left: 7px;' : null;
  return `<span style="${style}">${source}</span>`;
}

/**
 * Removes unnecessary columns and also renames them.
 * @param transactionData {Array.<Object>} An array containing each row for transactions
 * @returns {Array.<Object>} The new list of transactions
 */
function toGsheets(transactionData) {
  return transactionData.map((row) => cleanTransactionRow(row));
}

/**
 * AG Grid date sorter that works with UTC datetime string
 * @param a {string} first date value being compared. Format: YYYY-MM-DDTHH:mm:ssZ
 * @param b {string} second date value being compared. Format: YYYY-MM-DDTHH:mm:ssZ
 * @return {number} The difference between the two values being compared.
 */
function dateSorter(a, b) {
  return new Date(a) - new Date(b);
}

/**
 *  * AG Grid sorter for sorting the transaction id embedded into a string html link.
 * @param valueA {String} First unused value in the cell
 * @param valueB {String} Second unused value in the cell
 * @param nodeA {Object} The AG Grid row a containing the `data`
 * @param nodeB {Object} The AG Grid row b containing the `data`
 * @return {number} The difference between the two values being compared (transactionId).
 */
function transactionIdHtmlSorter(valueA, valueB, nodeA, nodeB) {
  return (
    parseInt(nodeA.data.transactionId, 10)
    - parseInt(nodeB.data.transactionId, 10)
  );
}

/**
 * Create the grid settings object required for AG Grid. Convert a
 * list of transactions objects into a list of lists for AG Grid's
 * consumption.
 * @returns {object}
 */
function getGridSettings(transactionsData) {
  const gridOptions = {
    defaultColDef: {
      resizable: true,
      sortable: true,
      width: 130,
    },
    columnDefs: [
      {
        headerName: 'Id',
        field: 'refundLink',
        cellRenderer: (cell) => cell.value,
        comparator: transactionIdHtmlSorter,
      },
      {
        headerName: 'Created (UTC)',
        field: 'createdAt',
        valueFormatter: utils.formatDatetimeShort,
        tooltipValueGetter: utils.formatDatetimeShortLocal,
        comparator: dateSorter,
        getQuickFilterText: utils.formatDatetimeShort,
        width: 200,
      },
      { headerName: 'Type', field: 'typeString' },
      { headerName: 'Source', field: 'source', cellRenderer: (cell) => getManualSourceHtml(cell.value) },
      {
        headerName: 'Amount',
        field: 'amount',
        valueFormatter: utils.formatCurrency,
        getQuickFilterText: utils.formatCurrency,
      },
      {
        headerName: 'Tax',
        field: 'taxAmount',
        valueFormatter: utils.formatCurrency,
        getQuickFilterText: utils.formatCurrency,
      },
      { headerName: 'Unit Qty', field: 'unitQuantity', sortable: false },
      {
        headerName: 'Plan Id',
        field: 'planIdObject',
        sortable: false,
        valueFormatter: (cell) => utils.dashIfEmpty(cell, 'planId'),
        getQuickFilterText: (cell) => utils.dashIfEmpty(cell, 'planId'),
      },
      {
        headerName: 'Products',
        field: 'productsHtml',
        sortable: false,
        cellRenderer: (cell) => cell.value,
      },
      {
        headerName: 'Stripe',
        field: 'htmlLink',
        sortable: false,
        cellRenderer: (cell) => cell.value,
      },
      {
        headerName: 'Description',
        field: 'description',
        sortable: false,
        cellStyle: { fontSize: '0.65rem' },
        width: 500,
      },
    ],
    pagination: true,
    paginationPageSize: 50,
    domLayout: 'autoHeight',
    enableCellTextSelection: true,
    onFirstDataRendered: (event) => event.api.sizeColumnsToFit(),
    onGridSizeChanged: (event) => event.api.sizeColumnsToFit(),
  };

  return {
    data: transactionsData,
    gridOptions,
  };
}

export default {
  toGsheets, cleanTransactionRow, getGridSettings, getManualSourceHtml,
};
